import {Injectable, isDevMode, PLATFORM_ID, Inject, TransferState, makeStateKey, StateKey} from '@angular/core';
import {isPlatformServer} from '@angular/common';
import {HttpClient} from "@angular/common/http";
import {BlogList} from "../models/blogList.model";
import {Observable, of, tap, switchMap} from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class WordpressService {
  baseUrl = "/blog";
  private isServer: boolean;

  constructor(
    private http: HttpClient,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    this.isServer = isPlatformServer(platformId);
    if (!isDevMode()) {
      this.baseUrl = this.isServer
        ? 'http://localhost:4100/api/wp-proxy'
        : 'https://blog.dart-rock-cafe.de/wp-json/wp/v2';
    }
  }

  private cachedGet<T>(key: StateKey<T>, url: string): Observable<T> {
    if (!this.isServer && this.transferState.hasKey(key)) {
      const data = this.transferState.get(key, null as unknown as T);
      this.transferState.remove(key);
      return of(data);
    }
    return this.http.get<T>(url).pipe(
      tap(data => {
        if (this.isServer) {
          this.transferState.set(key, data);
        }
      })
    );
  }

  getPosts(limit: number = 4) {
    const key = makeStateKey<BlogList[]>(`wp-posts-cat4-${limit}`);
    return this.cachedGet(key, `${this.baseUrl}/posts?categories=4&per_page=${limit}&page=1&_embed&orderby=date&order=desc`);
  }

  getArticles(limit: number = 4) {
    const key = makeStateKey<BlogList[]>(`wp-articles-cat5-${limit}`);
    return this.cachedGet(key, `${this.baseUrl}/posts?categories=5&per_page=${limit}&page=1&_embed&orderby=date&order=desc`);
  }

  getImageById(id: number) {
    return this.http.get<BlogList[]>(`${this.baseUrl}/media/${id}`);
  }

  getPostBySlug(slug: string) {
    const key = makeStateKey<BlogList[]>(`wp-post-${slug}`);
    return this.cachedGet(key, `${this.baseUrl}/posts?slug=${slug}&_embed`);
  }

  getAllPosts(page = 1, limit = 100) {
    const key = makeStateKey<BlogList[]>(`wp-all-posts-${page}-${limit}`);
    return this.cachedGet(key, `${this.baseUrl}/posts?&per_page=${limit}&page=${page}&_embed`);
  }

  getRecentPosts(limit: number, exclude: number | undefined) {
    let url = `${this.baseUrl}/posts?&per_page=${limit}&page=1&_embed`
    if (exclude) url += `&exclude=${exclude}`
    const key = makeStateKey<BlogList[]>(`wp-recent-${limit}-${exclude}`);
    return this.cachedGet(key, url);
  }

  getRelatedPosts(params: {
    limit: number;
    includeCategories?: number[];
    excludeCategories?: number[];
    excludeIds?: number[];
  }): Observable<BlogList[]> {
    const parts: string[] = [
      `per_page=${params.limit}`,
      `page=1`,
      `_embed`,
      `orderby=date`,
      `order=desc`,
    ];
    if (params.includeCategories?.length) {
      parts.push(`categories=${params.includeCategories.join(',')}`);
    }
    if (params.excludeCategories?.length) {
      parts.push(`categories_exclude=${params.excludeCategories.join(',')}`);
    }
    if (params.excludeIds?.length) {
      parts.push(`exclude=${params.excludeIds.join(',')}`);
    }
    const url = `${this.baseUrl}/posts?${parts.join('&')}`;
    const key = makeStateKey<BlogList[]>(`wp-related-${parts.join('|')}`);
    return this.cachedGet(key, url);
  }

  getRandomRatings(limit: number = 10) {
    let url = `${this.baseUrl}/bewertungen?per_page=${limit}&page=1&_embed&orderby=rand`
    return this.http.get<BlogList[]>(url);
  }

  getCategories(): Observable<any[]> {
    const key = makeStateKey<any[]>('wp-categories');
    return this.cachedGet(key, `${this.baseUrl}/categories?per_page=100&hide_empty=true&orderby=name&order=asc`);
  }

  getCategoryBySlug(slug: string): Observable<any[]> {
    const key = makeStateKey<any[]>(`wp-category-${slug}`);
    return this.cachedGet(key, `${this.baseUrl}/categories?slug=${slug}`);
  }

  getPostsByCategory(categoryId: number, limit: number = 100): Observable<BlogList[]> {
    const key = makeStateKey<BlogList[]>(`wp-posts-cat-${categoryId}-${limit}`);
    return this.cachedGet(key, `${this.baseUrl}/posts?categories=${categoryId}&per_page=${limit}&page=1&_embed&orderby=date&order=desc`);
  }

  getPostsByCategorySlug(slug: string, limit: number = 100): Observable<BlogList[]> {
    return this.getCategoryBySlug(slug).pipe(
      switchMap(categories => {
        if (categories && categories.length > 0) {
          return this.getPostsByCategory(categories[0].id, limit);
        }
        return of([]);
      })
    );
  }
}
